import ApplicationIcon from '@mui/icons-material/Assignment';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import CloseIcon from '@mui/icons-material/Close';
import DocIcon from '@mui/icons-material/Description';
import ProposalIcon from '@mui/icons-material/Description';
import DrawIcon from '@mui/icons-material/Draw';
import FolderIcon from '@mui/icons-material/Folder';
import GavelIcon from '@mui/icons-material/Gavel';
import GroupsIcon from '@mui/icons-material/Groups';
import HandshakeIcon from '@mui/icons-material/Handshake';
import HomeIcon from '@mui/icons-material/Home';
import MessageIcon from '@mui/icons-material/Message';
import PersonIcon from '@mui/icons-material/Person';
import SendIcon from '@mui/icons-material/Send';
import TimelineIcon from '@mui/icons-material/Timeline';
import UploadIcon from '@mui/icons-material/Upload';
import WarningIcon from '@mui/icons-material/Warning';
import {
  type ActivityEvent,
  Avatar,
  Box,
  ButtonBase,
  DataRoomEmptyState,
  DataRoomPanel,
  type DataRoomPanelSourceGroup,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Divider,
  EmptyState,
  EntityActivityTimeline,
  formatCurrencyWhole,
  formatRate,
  GlassAlert,
  GlassButton,
  GlassCard,
  GlassChip,
  GlassIconButton,
  GlassTabs,
  GlassTextField,
  IntakeSubmissionSummary,
  IntakeWizardScroll,
  LinearProgress,
  List,
  ListItem,
  ListItemIcon,
  ListItemText,
  MagicInput,
  MagicSelect,
  NeonAccentKey,
  ShimmerLoader,
  SplitPanel,
  Stack,
  snakeToTitle,
  Typography,
  useI18n,
} from '@ops-matrix/design-system-ui';
import {
  CaseStatus,
  CONSUMER_STEPPER_STAGES,
  ConsumerLoanStage,
  DOCUMENT_SCOPE_MAP,
  type DocumentType,
  getConsumerLoanStageLabelKey,
  getGuaranteeDescription,
  getRoleDescription,
  type IntakeFlowType,
  isTerminalLoanStage,
  type LoanStage,
  type LoanSubstage,
  toConsumerLoanStage,
} from '@ops-matrix/shared-types';
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useLocation, useNavigate, useParams, useSearchParams } from 'react-router-dom';
import { ConsumerBrowsePanel } from '../../components/data-room/ConsumerBrowsePanel';
import { ConsumerUploadPanel } from '../../components/data-room/ConsumerUploadPanel';
import { DocumentSigningModal, SigningStatusDisplay } from '../../components/esign';
import { PageTemplate } from '../../components/layout/PageTemplate';
import { AstorAvatar } from '../../components/loans/AstorAvatar';
import { AstorBriefingCard } from '../../components/loans/AstorBriefingCard';
import { AstorStepper } from '../../components/loans/AstorStepper';
import { MyPropertyTab } from '../../components/loans/MyPropertyTab';
import { useBreadcrumbOverrides } from '../../contexts/BreadcrumbContext';
import { useCase, useUploadDocument } from '../../hooks';
import { useIntakeTemplate } from '../../hooks/useIntakeTemplate';
import { trpc } from '../../lib/trpc/client';
import {
  useCaseBorrowers,
  useCaseGuarantors,
  useConsumerCaseActivity,
  useConsumerMessages,
  useConsumerPipelineStatus,
  useEsignSubmissions,
  useMySubmission,
  useProposalsByCase,
  useResubmitApplication,
  useSendConsumerMessage,
  useWithdrawApplication,
} from '../../lib/trpc/hooks';
import { formatFileSize } from '../../lib/utils/format-number';

// Document category keys for upload
const DOCUMENT_CATEGORY_KEYS = [
  { value: 'identity', labelKey: 'documents.category.identity' },
  { value: 'income', labelKey: 'documents.category.income' },
  { value: 'address', labelKey: 'documents.category.address' },
  { value: 'bank_statement', labelKey: 'documents.category.bankStatement' },
  { value: 'other', labelKey: 'documents.category.other' },
] as const;

// Borrower-facing pipeline driven by the canonical `LoanStage` in shared-types,
// projected onto the simplified 7-stage consumer view (APPLIED → REVIEWING →
// OFFER → ACCEPTED → CLOSING → FUNDED → ACTIVE). Operational nuance (credit
// memos, committee reviews, etc.) is intentionally hidden — borrowers want
// "where is my loan?", not internal underwriting state.
const CONSUMER_PIPELINE_STEP_KEYS = CONSUMER_STEPPER_STAGES.filter((s) => s !== ConsumerLoanStage.TERMINAL).map(
  (stage) => getConsumerLoanStageLabelKey(stage),
);

function resolveConsumerStep(
  loanStage: string | null | undefined,
  loanSubstage: string | null | undefined,
  legacyStatus: string | null | undefined,
): number {
  // Prefer the canonical loanStage when the backend sets it.
  if (loanStage) {
    if (isTerminalLoanStage(loanStage)) return -1;
    const consumerStage = toConsumerLoanStage(
      loanStage as LoanStage,
      (loanSubstage as LoanSubstage | undefined) ?? undefined,
    );
    if (consumerStage === ConsumerLoanStage.TERMINAL) return -1;
    return CONSUMER_STEPPER_STAGES.indexOf(consumerStage);
  }
  // Legacy fallback for cases pre-dating the loan-stage migration. Maps the
  // old per-business `CaseStatus` onto the nearest consumer stage so the
  // stepper still renders something sane.
  switch (legacyStatus) {
    case CaseStatus.NEW:
    case CaseStatus.IN_PROGRESS:
      return 0;
    case CaseStatus.PENDING_REVIEW:
    case CaseStatus.UNDER_REVIEW:
      return 1;
    case CaseStatus.PENDING_APPROVAL:
      return 3;
    case CaseStatus.APPROVED:
      return 3;
    default:
      return 0;
  }
}

/** Terminal statuses that don't map to a pipeline step */
const TERMINAL_STATUSES: readonly string[] = [
  CaseStatus.REJECTED,
  CaseStatus.CLOSED,
  CaseStatus.CANCELLED,
  CaseStatus.ARCHIVED,
  'withdrawn',
];

const PENDING_SUBMITTER_STATUSES: ReadonlySet<string> = new Set(['pending', 'sent', 'opened']);

function isTerminalStatus(status: string): boolean {
  return TERMINAL_STATUSES.includes(status);
}

/** Get status color for chips. Covers both the legacy `CaseStatus` names and
 *  the new canonical `LoanStage` values so the chip renders sensibly whether
 *  the backend is returning the new fields or not. */
function getApplicationStatusColor(
  status: string | null | undefined,
): 'default' | 'primary' | 'secondary' | 'error' | 'info' | 'success' | 'warning' {
  switch (status?.toLowerCase()) {
    case 'new':
    case 'submitted':
    case 'lead':
    case 'application':
      return 'info';
    case 'in_progress':
    case 'pending':
    case 'pending_review':
    case 'under_review':
    case 'reviewing':
    case 'qualified':
    case 'underwriting':
    case 'term_sheet':
      return 'primary';
    case 'pending_approval':
    case 'closing':
    case 'approval':
      return 'warning';
    case 'approved':
    case 'converted':
    case 'funded':
    case 'completed':
    case 'servicing':
    case 'paid_off':
      return 'success';
    case 'rejected':
    case 'cancelled':
    case 'declined':
    case 'charged_off':
      return 'error';
    case 'withdrawn':
    case 'closed':
    case 'archived':
    case 'expired':
      return 'default';
    default:
      return 'default';
  }
}

/**
 * Metadata fields to exclude from submission display.
 * These are system/tracking fields, not user-submitted form values.
 * @see consumer.ts submitApplication for how these are stored
 */
const CASE_METADATA_SYSTEM_FIELDS = [
  // Template tracking
  'templateId',
  'intakeTemplateId',
  'templateVersion',
  'intakeTemplateVersion',
  'intakeType',
  // Content fields
  'title',
  'description',
  // Tracking/attribution
  'referralMetadata',
  'partnerAttribution',
  'utmSource',
  'utmMedium',
  'utmCampaign',
  'sourceBusinessId',
  'referralCode',
  'referralPartnerId',
  'referralPartnerName',
  'trackingCodeId',
  // E-sign/submission metadata
  'signedDocuments',
  'signedAt',
  'submittedVia',
  'submittedAt',
  // Preserved raw data (complete submission blob for data integrity)
  '_submissionData',
  // Duplicate fields (we use firstName/lastName/email/phone instead)
  'applicantName',
  'applicantEmail',
  'applicantPhone',
] as const;

export default function CaseDetailPage() {
  const navigate = useNavigate();
  const location = useLocation();
  const { caseId } = useParams();
  const { t } = useI18n();
  const [signingModalOpen, setSigningModalOpen] = useState(false);
  const [selectedSubmitterId, setSelectedSubmitterId] = useState<number | null>(null);
  const [searchParams] = useSearchParams();
  const [activeTab, setActiveTab] = useState<string>(searchParams.get('tab') || 'application');

  useEffect(() => {
    const nextTab = searchParams.get('tab') || 'application';
    setActiveTab((currentTab) => (currentTab === nextTab ? currentTab : nextTab));
  }, [searchParams]);

  // Determine view type: location state hint OR auto-detect from API responses.
  // When navigating from the list, location.state provides a hint. When navigating
  // directly via URL, we try both APIs and use whichever succeeds.
  const locationSource = (location.state as { source?: string } | null)?.source;
  const hintIsSubmission = locationSource === 'submission';

  // Upload state (used by Documents tab dialog AND Data Room upload panel)
  const [uploadDialogOpen, setUploadDialogOpen] = useState(false);
  const [selectedFiles, setSelectedFiles] = useState<File[]>([]);
  const [selectedCategory, setSelectedCategory] = useState('other');
  const [uploadProgress, setUploadProgress] = useState<Record<string, number>>({});
  const uploadMutation = useUploadDocument();

  // Data Room upload panel state
  const [dataRoomUploadOpen, setDataRoomUploadOpen] = useState(false);
  const [drIsUploading, setDrIsUploading] = useState(false);

  // Browse & Link state (G9)
  const [browseOpen, setBrowseOpen] = useState(false);
  const [pendingUploadDocType, setPendingUploadDocType] = useState<string | null>(null);

  // Messages state
  const [messageInput, setMessageInput] = useState('');
  const messagesEndRef = useRef<HTMLDivElement>(null);
  const sendMessageMutation = useSendConsumerMessage();

  // Withdraw state (Issue #809)
  const [withdrawDialogOpen, setWithdrawDialogOpen] = useState(false);
  const [withdrawReason, setWithdrawReason] = useState('');
  const [withdrawAlert, setWithdrawAlert] = useState<{ type: 'success' | 'error'; message: string } | null>(null);
  const withdrawMutation = useWithdrawApplication();

  // Resubmit state (for rejected applications)
  const [resubmitError, setResubmitError] = useState<string | null>(null);

  // Data Room source group expand/collapse state
  const [expandedSources, setExpandedSources] = useState<Record<string, boolean>>({});
  const resubmitMutation = useResubmitApplication();

  // Always attempt both fetches; one will fail gracefully (NOT_FOUND) and the other succeeds.
  // This avoids relying on ephemeral location.state which is lost on direct URL navigation.
  const {
    data: caseDataDirect,
    isPending: casePending,
    error: caseError,
    refetch: refetchCase,
  } = useCase(caseId ?? '');

  const {
    data: submissionData,
    isPending: submissionPending,
    error: submissionError,
    refetch: refetchSubmission,
  } = useMySubmission(caseId ?? null);

  // If the submission was converted to a case, also fetch the case using the converted ID.
  // This allows the consumer to see the full case view (messages, documents, activity) even
  // when navigating via the original submission URL.
  const convertedCaseId = submissionData?.convertedToCaseId;
  const { data: convertedCaseData } = useCase(convertedCaseId ?? '');

  // Use converted case data if available, otherwise direct case data
  const caseData = convertedCaseData || caseDataDirect;

  // Auto-detect: show case view if we have case data (including from conversion),
  // otherwise show submission view
  const isSubmissionView = caseData ? false : submissionData ? true : !hintIsSubmission ? false : true;

  // Consider the page pending while either underlying query is still loading
  const isPending = casePending || submissionPending;
  const error =
    !casePending && !submissionPending && !caseData && !submissionData && caseError && submissionError
      ? submissionError
      : null;

  // Messages: fetch for case views (use converted case ID if available)
  const effectiveCaseId = convertedCaseId || caseId;
  const { data: messagesData, isLoading: messagesLoading } = useConsumerMessages(
    !isSubmissionView && caseData ? (effectiveCaseId ?? null) : null,
  );

  // Extract template info from case metadata or submission data (Issue #475)
  const caseMetadata = caseData?.metadata as Record<string, unknown> | undefined;
  // Backend stores template ID as 'intakeTemplateId' (see consumer.ts submitApplication)
  const templateId = isSubmissionView
    ? submissionData?.templateId
    : ((caseMetadata?.intakeTemplateId ?? caseMetadata?.templateId ?? submissionData?.templateId) as
        | string
        | undefined);
  const templateType = isSubmissionView
    ? (submissionData?.intakeType as IntakeFlowType | undefined)
    : ((caseMetadata?.intakeType ?? submissionData?.intakeType) as IntakeFlowType | undefined);

  // Fetch the intake template if available (must be at top level per Rules of Hooks)
  const { template, isLoading: templateLoading } = useIntakeTemplate({
    templateId: templateId || undefined,
    templateType,
    enabled: Boolean(templateId || templateType),
  });

  // Extract submission values from metadata or submission formData (exclude system fields)
  // Backend stores: { _submissionData: <original wizard values>, ...flatFields }
  // The _submissionData has correct types (address objects, numbers), but flat fields
  // that follow may overwrite with strings. We prefer _submissionData then patch gaps.
  const submissionValues = useMemo(() => {
    // For submission view, use the formData from the submission endpoint
    if (isSubmissionView) {
      if (!submissionData?.formData) return null;
      const stored = submissionData.formData as Record<string, unknown>;
      if (Object.keys(stored).length === 0) return null;

      // Prefer _submissionData (original wizard values with correct field types)
      const rawWizard = stored._submissionData as Record<string, unknown> | undefined;
      const hasWizardData = rawWizard && typeof rawWizard === 'object' && Object.keys(rawWizard).length > 0;
      const values: Record<string, unknown> = hasWizardData ? { ...rawWizard } : { ...stored };

      // Remove system/metadata fields that shouldn't be form values
      const systemFields: readonly string[] = CASE_METADATA_SYSTEM_FIELDS;
      for (const key of systemFields) {
        delete values[key];
      }

      // --- Fix address fields: IntakeWizardScroll expects { street1, city, state, postalCode } ---
      // Seeded data has flat strings; wizard-submitted data may have an object via _submissionData.
      // Also pull from top-level submissionData response fields as fallback.
      if (typeof values.propertyAddress === 'string' || !values.propertyAddress) {
        const street =
          (typeof values.propertyAddress === 'string' ? values.propertyAddress : '') ||
          (stored.propertyAddress as string) ||
          submissionData.propertyAddress ||
          '';
        const city =
          (values.propertyCity as string) || (stored.propertyCity as string) || submissionData.propertyCity || '';
        const state =
          (values.propertyState as string) || (stored.propertyState as string) || submissionData.propertyState || '';
        const zip =
          (values.propertyZip as string) || (stored.propertyZip as string) || submissionData.propertyZip || '';
        if (street || city || state || zip) {
          values.propertyAddress = { street1: street, city, state, postalCode: zip };
        }
      }
      // Similarly reconstruct guarantorAddress if flat
      if (typeof values.guarantorAddress === 'string' || (!values.guarantorAddress && stored.guarantorAddress)) {
        const addr = (values.guarantorAddress as string) || (stored.guarantorAddress as string) || '';
        if (addr) {
          values.guarantorAddress = { street1: addr, city: '', state: '', postalCode: '' };
        }
      }
      // Clean up flat address keys (not wizard field names)
      delete values.propertyCity;
      delete values.propertyState;
      delete values.propertyZip;

      // --- Fix currency fields: wizard expects numbers, backend may store strings ---
      const currencyFields = [
        'purchasePrice',
        'rehabBudget',
        'afterRepairValue',
        'requestedLoanAmount',
        'loanAmount',
        'liquidReserves',
      ];
      for (const field of currencyFields) {
        const v = values[field] ?? stored[field];
        if (v !== undefined && v !== null && v !== '') {
          const num = typeof v === 'number' ? v : parseFloat(String(v).replace(/[^0-9.]/g, ''));
          if (!Number.isNaN(num)) values[field] = num;
        }
      }

      // --- Map field-name differences between backend storage and template field names ---

      // loanAmount (backend) → requestedLoanAmount (template field name)
      if (values.requestedLoanAmount === undefined && values.loanAmount !== undefined) {
        values.requestedLoanAmount = values.loanAmount;
      }
      // Also use the top-level submission loanAmount if form values don't have it
      if (values.requestedLoanAmount === undefined && submissionData.loanAmount) {
        const num = parseFloat(String(submissionData.loanAmount).replace(/[^0-9.]/g, ''));
        if (!Number.isNaN(num)) values.requestedLoanAmount = num;
      }

      // creditScore / creditScoreRange → estimatedFico (template select options: "760+", "740-759", etc.)
      if (values.estimatedFico === undefined) {
        const rawScore = values.creditScoreRange ?? values.creditScore ?? stored.creditScoreRange ?? stored.creditScore;
        if (rawScore !== undefined && rawScore !== null) {
          const scoreStr = String(rawScore);
          // If it's already a range option value, use as-is
          if (scoreStr.includes('-') || scoreStr.includes('+') || scoreStr.startsWith('below')) {
            values.estimatedFico = scoreStr;
          } else {
            // Numeric score → convert to range bucket
            const n = parseInt(scoreStr, 10);
            if (!Number.isNaN(n)) {
              if (n >= 760) values.estimatedFico = '760+';
              else if (n >= 740) values.estimatedFico = '740-759';
              else if (n >= 720) values.estimatedFico = '720-739';
              else if (n >= 700) values.estimatedFico = '700-719';
              else if (n >= 680) values.estimatedFico = '680-699';
              else if (n >= 660) values.estimatedFico = '660-679';
              else values.estimatedFico = 'below-660';
            }
          }
        }
      }

      // completedFlips / experience → flipsInLast2Years (select options: "0", "1-3", "4-10", "11+")
      if (values.flipsInLast2Years === undefined) {
        const rawFlips = values.completedFlips ?? stored.completedFlips ?? values.experience ?? stored.experience;
        if (rawFlips !== undefined && rawFlips !== null) {
          values.flipsInLast2Years = rawFlips;
        }
      }
      // Convert numeric value (number or parseable string) to the select option range
      if (values.flipsInLast2Years !== undefined) {
        const flipsStr = String(values.flipsInLast2Years);
        // Already a valid range → keep it
        if (!['0', '1-3', '4-10', '11+'].includes(flipsStr)) {
          const n = parseInt(flipsStr, 10);
          if (!Number.isNaN(n)) {
            if (n === 0) values.flipsInLast2Years = '0';
            else if (n <= 3) values.flipsInLast2Years = '1-3';
            else if (n <= 10) values.flipsInLast2Years = '4-10';
            else values.flipsInLast2Years = '11+';
          }
        }
      }

      // timelineMonths → preferredTerm (select options: "6", "9", "12", "18", "24")
      if (values.preferredTerm === undefined && values.timelineMonths !== undefined) {
        const months = parseInt(String(values.timelineMonths), 10);
        if (!Number.isNaN(months)) {
          // Find the nearest valid option
          const options = [6, 9, 12, 18, 24];
          const nearest = options.reduce((a, b) => (Math.abs(b - months) < Math.abs(a - months) ? b : a));
          values.preferredTerm = String(nearest);
        }
      }

      // investmentType → if missing, try propertyType
      if (values.investmentType === undefined && values.propertyType !== undefined) {
        values.investmentType = values.propertyType;
      }

      // Backfill basic fields from stored flat data if _submissionData was missing them
      if (!values.firstName && stored.firstName) values.firstName = stored.firstName;
      if (!values.lastName && stored.lastName) values.lastName = stored.lastName;
      if (!values.email && stored.email) values.email = stored.email;
      if (!values.phone && stored.phone) values.phone = stored.phone;
      if (!values.entityType && stored.entityType) values.entityType = stored.entityType;
      if (!values.entityName && stored.entityName) values.entityName = stored.entityName;

      return values;
    }

    // Case view: extract values from case metadata
    const systemFields: readonly string[] = CASE_METADATA_SYSTEM_FIELDS;
    const values: Record<string, unknown> = {};

    if (caseMetadata) {
      for (const [key, val] of Object.entries(caseMetadata)) {
        if (systemFields.includes(key) || val === undefined || val === null || val === '') {
          continue;
        }
        values[key] = val;
      }

      // Backward compatibility: if new fields don't exist but old ones do, map them
      if (!values['firstName'] && caseMetadata['applicantName']) {
        const name = caseMetadata['applicantName'] as string;
        const nameParts = name.trim().split(/\s+/);
        values['firstName'] = nameParts[0] || '';
        values['lastName'] = nameParts.slice(1).join(' ') || '';
      }
      if (!values['email'] && caseMetadata['applicantEmail']) {
        values['email'] = caseMetadata['applicantEmail'];
      }
      if (!values['phone'] && caseMetadata['applicantPhone']) {
        values['phone'] = caseMetadata['applicantPhone'];
      }
    }

    // Fallback: if case metadata is empty, use submission formData (e.g. case created
    // from intake submission where metadata wasn't populated during conversion)
    if (Object.keys(values).length === 0 && submissionData?.formData) {
      const stored = submissionData.formData as Record<string, unknown>;
      const rawWizard = stored._submissionData as Record<string, unknown> | undefined;
      const hasWizardData = rawWizard && typeof rawWizard === 'object' && Object.keys(rawWizard).length > 0;
      const fallbackValues = hasWizardData ? { ...rawWizard } : { ...stored };

      // Also backfill top-level submission fields
      if (!fallbackValues['loanAmount'] && submissionData.loanAmount)
        fallbackValues['loanAmount'] = submissionData.loanAmount;
      if (fallbackValues['requestedLoanAmount'] === undefined && submissionData.loanAmount) {
        fallbackValues['requestedLoanAmount'] = submissionData.loanAmount;
      }
      if (!fallbackValues['propertyAddress'] && submissionData.propertyAddress) {
        fallbackValues['propertyAddress'] = submissionData.propertyAddress;
      }
      if (!fallbackValues['propertyType'] && submissionData.propertyType) {
        fallbackValues['propertyType'] = submissionData.propertyType;
      }

      return Object.keys(fallbackValues).length > 0 ? fallbackValues : null;
    }

    return Object.keys(values).length > 0 ? values : null;
  }, [caseMetadata, isSubmissionView, submissionData]);

  // Handle resubmission of a rejected application (updates existing submission, not create new)
  const handleResubmit = useCallback(
    async (formData: Record<string, unknown>) => {
      if (!submissionData?.id) {
        setResubmitError(t('application.resubmitFailed'));
        return;
      }

      setResubmitError(null);
      const toAmountString = (value: unknown): string | undefined => {
        if (value === undefined || value === null || value === '') return undefined;
        if (typeof value === 'number') return String(value);
        if (typeof value === 'string' && value.trim()) return value;
        return undefined;
      };

      try {
        const propertyAddress = formData.propertyAddress as
          | { street1?: string; city?: string; state?: string; postalCode?: string }
          | string
          | undefined;
        const addressStr = typeof propertyAddress === 'string' ? propertyAddress : propertyAddress?.street1;
        const cityStr = typeof propertyAddress === 'string' ? undefined : propertyAddress?.city;
        const stateStr = typeof propertyAddress === 'string' ? undefined : propertyAddress?.state;
        const zipStr = typeof propertyAddress === 'string' ? undefined : propertyAddress?.postalCode;

        const loanAmount =
          toAmountString(formData.requestedLoanAmount) ||
          toAmountString(formData.estimatedLoanAmount) ||
          toAmountString(formData.loanAmount) ||
          undefined;

        await resubmitMutation.mutateAsync({
          submissionId: submissionData.id,
          formValues: formData,
          firstName: (formData.firstName as string) || '',
          lastName: (formData.lastName as string) || '',
          email: (formData.email as string) || '',
          phone: (formData.phone as string) || '',
          propertyType: (formData.investmentType as string) || undefined,
          propertyAddress: addressStr,
          propertyCity: cityStr || (formData.propertyCity as string) || undefined,
          propertyState: stateStr || (formData.propertyState as string) || undefined,
          propertyZip: zipStr || (formData.propertyZip as string) || undefined,
          loanAmount,
          entityType: (formData.entityType as string) || undefined,
          purchasePrice: toAmountString(formData.purchasePrice),
          afterRepairValue: toAmountString(formData.afterRepairValue),
          rehabBudget: toAmountString(formData.rehabBudget),
          signedDocuments: formData.complianceConfirmed ? ['credit_auth', 'privacy_policy', 'econsent'] : [],
        });

        // Refetch the submission to show updated status
        void refetchSubmission();
        // Navigate to applications list with success indicator
        navigate('/applications', { state: { resubmitted: true } });
      } catch (err) {
        setResubmitError(err instanceof Error ? err.message : t('application.resubmitFailed'));
      }
    },
    [submissionData, resubmitMutation, navigate, t, refetchSubmission],
  );

  // Fetch activity timeline for this case (Issue #234) — skip for submission views
  // Hook must be called unconditionally at the top level (Rules of Hooks)
  const { data: activityData } = useConsumerCaseActivity(caseData ? (effectiveCaseId ?? null) : null, { limit: 50 });

  // Document checklist is now merged into Data Room tab (Issue #1145)

  // Fetch pipeline status for SLA/timeline visibility (Item #14)
  // Pass effectiveCaseId so multi-case consumers see the correct pipeline for this case
  const { data: pipelineStatus } = useConsumerPipelineStatus(effectiveCaseId);

  // Fetch Data Room documents for this case (Item #5 - Interconnected Data Room)
  const {
    data: dataRoomData,
    refetch: refetchDataRoom,
    isLoading: dataRoomLoading,
    error: dataRoomError,
  } = trpc.dataRoom.view.useQuery({ caseId: effectiveCaseId! }, { enabled: !!effectiveCaseId && !!caseData });

  // Data Room link mutation
  const linkDocumentMutation = trpc.dataRoom.linkDocument.useMutation({
    onSuccess: () => {
      void refetchDataRoom();
      void refetchCase();
      setBrowseOpen(false);
    },
  });

  // Data Room document view handler — navigates to unified document view page
  const handleViewDocument = useCallback(
    (documentId: string) => {
      navigate(`/documents/${documentId}/view`);
    },
    [navigate],
  );

  // Fetch e-sign submissions for this case (Issue #300) — skip for submission views
  const { data: esignSubmissions, refetch: refetchEsignSubmissions } = useEsignSubmissions(
    caseData && effectiveCaseId ? { caseId: effectiveCaseId } : undefined,
  );

  // Fetch proposals linked to this case/application (Issue #475) — skip for submission views
  const { data: proposalsData, isLoading: proposalsLoading } = useProposalsByCase(
    caseData ? (effectiveCaseId ?? null) : null,
  );

  // Fetch borrowers and guarantors for Parties tab (Gap #7, Item #11) — skip for submission views
  const { data: borrowersData } = useCaseBorrowers(caseData ? (effectiveCaseId ?? null) : null);
  const { data: guarantorsData } = useCaseGuarantors(caseData ? (effectiveCaseId ?? null) : null);

  // Map activity data to EntityActivityTimeline format (moved before early returns per Rules of Hooks)
  const timeline = useMemo((): ActivityEvent[] => {
    const rawTimeline = (activityData?.data ?? []) as {
      id: string;
      type: string;
      action?: string;
      description: string;
      createdAt: string;
      actorName?: string;
    }[];
    return rawTimeline.map((event) => ({
      id: event.id,
      type: event.type,
      action: event.action ?? event.type,
      description: event.description,
      createdAt: event.createdAt,
      actorName: event.actorName,
    }));
  }, [activityData]);

  // Upload handlers
  const handleUpload = async () => {
    if (selectedFiles.length === 0 || !effectiveCaseId) return;

    try {
      for (const file of selectedFiles) {
        setUploadProgress((prev) => ({ ...prev, [file.name]: 0 }));
        await uploadMutation.mutateAsync({
          file,
          caseId: effectiveCaseId,
          description: t('documents.uploadCategoryDescription', {
            category: t(
              DOCUMENT_CATEGORY_KEYS.find((c) => c.value === selectedCategory)?.labelKey ?? 'documents.category.other',
            ),
          }),
        });
        setUploadProgress((prev) => ({ ...prev, [file.name]: 100 }));
      }
      // Refetch case data and data room to show new documents
      await refetchCase();
      void refetchDataRoom();
      setUploadDialogOpen(false);
      setSelectedFiles([]);
      setSelectedCategory('other');
      setUploadProgress({});
    } catch (error) {
      console.error('Upload failed:', error);
    }
  };

  const handleRemoveFile = (index: number) => {
    setSelectedFiles((prev) => prev.filter((_, i) => i !== index));
  };

  // Data Room panel handlers (G9)
  const handleBrowseOpen = useCallback(() => {
    setDataRoomUploadOpen(false);
    setBrowseOpen(true);
  }, []);

  const handleBrowseClose = useCallback(() => {
    setBrowseOpen(false);
  }, []);

  const handleConsumerUploadOpen = useCallback(() => {
    setBrowseOpen(false);
    setDataRoomUploadOpen(true);
  }, []);

  const handleConsumerUploadClose = useCallback(() => {
    setDataRoomUploadOpen(false);
    setPendingUploadDocType(null);
    void refetchDataRoom();
  }, [refetchDataRoom]);

  const handleConsumerUpload = useCallback(
    async (files: File[], documentType: string | undefined, category: string | undefined) => {
      if (!effectiveCaseId) return;
      setDrIsUploading(true);
      try {
        for (const file of files) {
          await uploadMutation.mutateAsync({
            file,
            caseId: effectiveCaseId,
            documentType,
            category,
          });
        }
        await refetchCase();
        void refetchDataRoom();
        setDataRoomUploadOpen(false);
        setPendingUploadDocType(null);
      } catch (error) {
        console.error('Upload failed:', error);
      } finally {
        setDrIsUploading(false);
      }
    },
    [effectiveCaseId, uploadMutation, refetchCase, refetchDataRoom],
  );

  const handleLinkDocument = useCallback(
    (documentId: string, consumerId: string) => {
      if (!effectiveCaseId) return;
      linkDocumentMutation.mutate({
        caseId: effectiveCaseId,
        documentId,
        sourceEntityType: 'CONSUMER',
        sourceEntityId: consumerId,
      });
    },
    [effectiveCaseId, linkDocumentMutation],
  );

  // Set breadcrumb label override so the UUID segment shows the readable case/submission number
  const { setOverride } = useBreadcrumbOverrides();
  const breadcrumbLabel = caseData?.caseNumber
    ? caseData.caseNumber
    : caseData?.type
      ? caseData.type.charAt(0).toUpperCase() + caseData.type.slice(1)
      : submissionData?.submissionNumber
        ? submissionData.submissionNumber
        : submissionData?.templateName || null;
  useEffect(() => {
    if (caseId && breadcrumbLabel) {
      setOverride(caseId, breadcrumbLabel);
    }
  }, [caseId, breadcrumbLabel, setOverride]);

  if (isPending) {
    return (
      <PageTemplate variant="detail" titleKey="case.application" icon={ApplicationIcon}>
        <Box sx={{ display: 'flex', justifyContent: 'center', p: 4 }}>
          <ShimmerLoader variant="circular" width={40} height={40} />
        </Box>
      </PageTemplate>
    );
  }

  if (!caseData && !submissionData) {
    return (
      <PageTemplate variant="detail" titleKey="case.application" icon={ApplicationIcon}>
        <GlassAlert severity="error" visible={true}>
          {error ? t('case.loadError') : t('common.notFound')}
        </GlassAlert>
      </PageTemplate>
    );
  }

  // --- Submission Detail View ---
  if (isSubmissionView && submissionData) {
    const submissionStatus = submissionData.status || 'submitted';
    const isRejected = submissionStatus === 'rejected';
    const submissionDisplayNumber = submissionData.submissionNumber
      ? submissionData.submissionNumber
      : submissionData.id.slice(0, 8);

    return (
      <PageTemplate
        variant="detail"
        title={submissionData.templateName || t('application.submissionDetails')}
        description={submissionDisplayNumber}
        icon={ApplicationIcon}
      >
        {/* Status Banner */}
        {isRejected ? (
          <>
            {submissionData.rejectionReason && (
              <GlassAlert severity="error" visible={true} sx={{ mb: 2 }}>
                <Typography variant="subtitle2" gutterBottom>
                  {t('application.rejectionReason')}
                </Typography>
                <Typography variant="body2">{submissionData.rejectionReason}</Typography>
              </GlassAlert>
            )}
            <GlassAlert severity="warning" visible={true} sx={{ mb: 3 }}>
              {t('application.rejectedCanResubmit')}
            </GlassAlert>
          </>
        ) : (
          <GlassAlert severity="info" visible={true} sx={{ mb: 3 }}>
            {t('application.pendingReview')}
          </GlassAlert>
        )}
        {/* Resubmit error */}
        {resubmitError && (
          <GlassAlert severity="error" visible={true} sx={{ mb: 3 }} onClose={() => setResubmitError(null)}>
            {resubmitError}
          </GlassAlert>
        )}
        {/* Application Form — same form as new application, read-only unless rejected */}
        {template && !templateLoading ? (
          <IntakeWizardScroll
            template={template}
            initialValues={submissionValues || {}}
            disabled={!isRejected}
            isAuthenticated={true}
            onComplete={handleResubmit}
            onCancel={isRejected ? () => navigate('/applications') : undefined}
            isSubmitting={resubmitMutation.isPending}
            labels={
              isRejected
                ? { submit: t('application.resubmit'), cancel: t('common.cancel') }
                : { submit: t('application.submitted') }
            }
          />
        ) : templateLoading ? (
          <Box sx={{ display: 'flex', justifyContent: 'center', py: 3 }}>
            <ShimmerLoader variant="circular" width={24} height={24} />
          </Box>
        ) : (
          // Fallback: Display as key-value pairs when no template available
          <GlassCard neonAccent={NeonAccentKey.NEUTRAL}>
            <Typography variant="h6" sx={{ mb: 2 }}>
              {t('case.yourSubmission')}
            </Typography>
            {submissionValues ? (
              <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1.5 }}>
                {Object.entries(submissionValues).map(([key, value]) => (
                  <Box key={key}>
                    <Typography variant="caption" color="textSecondary" sx={{ textTransform: 'capitalize' }}>
                      {key.replace(/([A-Z])/g, ' $1').trim()}
                    </Typography>
                    <Typography variant="body2">
                      {typeof value === 'object' ? JSON.stringify(value) : String(value)}
                    </Typography>
                  </Box>
                ))}
              </Box>
            ) : (
              <Typography color="textSecondary">{t('application.noFormData')}</Typography>
            )}
          </GlassCard>
        )}
        {/* Back button */}
        <Box sx={{ mt: 3 }}>
          <GlassButton variant="outlined" onClick={() => navigate('/applications')}>
            {t('common.back')}
          </GlassButton>
        </Box>
      </PageTemplate>
    );
  }

  // --- Case Detail View (existing behavior) ---
  // caseData is guaranteed to be defined here (submission view returns early above,
  // and the !caseData guard returns early for case view)
  const caseDetail = caseData!;
  // New loan lifecycle fields — `caseDetail` may not carry them strictly typed
  // yet (gRPC response shape evolves independently of the tRPC type), so we
  // read defensively and fall back to legacy CaseStatus when missing.
  const loanStage = (caseDetail as Record<string, unknown>).loanStage as string | null | undefined;
  const loanSubstage = (caseDetail as Record<string, unknown>).loanSubstage as string | null | undefined;
  const currentStep = resolveConsumerStep(loanStage, loanSubstage, caseDetail.status);
  const terminalCase = loanStage ? isTerminalLoanStage(loanStage) : isTerminalStatus(caseDetail.status);
  // Gap 19: Extract assigneeName with proper typing instead of unsafe casts
  const assigneeName = (caseDetail as Record<string, unknown>).assigneeName as string | null | undefined;

  // GAP 9 (AC9.1-AC9.4): Referral partner attribution from case metadata
  const referralPartnerName = caseMetadata?.referralPartnerName as string | undefined;
  const referralPartnerId = caseMetadata?.referralPartnerId as string | undefined;

  // Translated pipeline step labels (7 borrower-facing stages)
  const pipelineSteps = CONSUMER_PIPELINE_STEP_KEYS.map((key) => t(key));

  /** Whether this application can be withdrawn */
  const canWithdraw =
    !terminalCase &&
    ['new', 'submitted', 'in_progress', 'pending', 'pending_review', 'reviewing'].includes(
      caseDetail.status?.toLowerCase(),
    );

  /** Handle withdraw button click — open confirmation dialog */
  const handleWithdrawClick = () => {
    setWithdrawReason('');
    setWithdrawDialogOpen(true);
  };

  /** Close withdraw dialog */
  const handleWithdrawClose = () => {
    setWithdrawDialogOpen(false);
    setWithdrawReason('');
  };

  /** Confirm withdrawal — calls the backend tRPC mutation */
  const handleWithdrawConfirm = async () => {
    if (!effectiveCaseId) return;

    try {
      await withdrawMutation.mutateAsync({
        caseId: effectiveCaseId,
        reason: withdrawReason || undefined,
      });
      setWithdrawDialogOpen(false);
      setWithdrawReason('');
      setWithdrawAlert({ type: 'success', message: t('application.withdrawn') });
      // Refetch case data so the UI reflects the new status
      void refetchCase();
    } catch (_err) {
      setWithdrawAlert({ type: 'error', message: t('application.withdrawError') });
    }
  };

  return (
    <PageTemplate
      variant="detail"
      title={t('consumer.loanDetail.title', { defaultValue: 'Document Request' })}
      description={
        caseDetail.caseNumber
          ? t('consumer.loanDetail.subtitle', {
              defaultValue: 'Loan number: {{caseNumber}}',
              caseNumber: caseDetail.caseNumber,
            })
          : caseDetail.id.slice(0, 8)
      }
      icon={ApplicationIcon}
      actions={
        <Stack direction="row" spacing={1}>
          <GlassButton variant="text" size="small" startIcon={<FolderIcon />} onClick={() => setActiveTab('dataRoom')}>
            {t('consumer.loanDetail.actions.documents', { defaultValue: 'Documents' })}
          </GlassButton>
          <GlassButton variant="text" size="small" startIcon={<MessageIcon />} onClick={() => setActiveTab('messages')}>
            {t('consumer.loanDetail.actions.messages', { defaultValue: 'Messages' })}
          </GlassButton>
          {canWithdraw && (
            <GlassButton variant="outlined" size="small" color="error" onClick={handleWithdrawClick}>
              {t('consumer.loanDetail.actions.withdraw', { defaultValue: 'Withdraw' })}
            </GlassButton>
          )}
        </Stack>
      }
      badges={
        <>
          {(loanStage === 'APPLIED' ||
            String(caseDetail.status || '').toLowerCase() === 'submitted' ||
            caseDetail.status === CaseStatus.NEW ||
            caseDetail.status === CaseStatus.IN_PROGRESS) && (
            <GlassChip
              size="small"
              label={t('consumer.loanDetail.badge.new', { defaultValue: 'New' })}
              color="success"
            />
          )}
          <GlassChip
            size="small"
            label={
              caseDetail.status
                ? t(`status.case.${caseDetail.status}`, caseDetail.status.replace(/_/g, ' '))
                : t('common.unknown')
            }
            color={getApplicationStatusColor(caseDetail.status)}
          />
          {caseDetail.priority && (
            <GlassChip
              size="small"
              label={t(`status.priority.${caseDetail.priority}`, caseDetail.priority)}
              variant="outlined"
              color="info"
            />
          )}
          {(referralPartnerName || referralPartnerId) && (
            <GlassChip
              icon={<HandshakeIcon />}
              size="small"
              label={
                referralPartnerName
                  ? t('case.referredBy', { partner: referralPartnerName })
                  : t('case.referredByPartner')
              }
              variant="outlined"
              color="secondary"
            />
          )}
        </>
      }
    >
      {/* Withdraw success/error alert */}
      {withdrawAlert && (
        <GlassAlert severity={withdrawAlert.type} visible={true} onClose={() => setWithdrawAlert(null)} sx={{ mb: 2 }}>
          {withdrawAlert.message}
        </GlassAlert>
      )}
      {/* Status Banner for terminal states. Severity maps DECLINED/CANCELLED
          (lender-driven adverse outcomes) to 'error' and everything else to
          'info' — `PAID_OFF` is a happy ending, not an error. */}
      {terminalCase && (
        <GlassAlert
          severity={
            loanStage === 'DECLINED' ||
            loanStage === 'CANCELLED' ||
            caseDetail.status === CaseStatus.REJECTED ||
            caseDetail.status === CaseStatus.CANCELLED
              ? 'error'
              : 'info'
          }
          visible={true}
          sx={{ mb: 2 }}
        >
          {loanStage
            ? t(`loan.stage.${loanStage.toLowerCase()}`, loanStage.replace(/_/g, ' '))
            : t(`application.status.${caseDetail.status}`, caseDetail.status.replace(/_/g, ' '))}
        </GlassAlert>
      )}
      {/* AstorBriefingCard — non-dismissible Astor banner (spec Astor surface #1) */}
      {!terminalCase && (
        <AstorBriefingCard
          headline={t('consumer.loanDetail.astor.briefingHeadline', {
            defaultValue: "I've pre-staged the documents your lender is most likely to ask for next.",
          })}
          body={t('consumer.loanDetail.astor.briefingBody', {
            defaultValue: 'Want to review them before they go to the lender? Nothing leaves until you say so.',
          })}
          primaryLabel={t('consumer.loanDetail.astor.reviewAndSend', { defaultValue: 'Review & send' })}
          secondaryLabel={t('consumer.loanDetail.astor.seeChanges', { defaultValue: 'See changes' })}
          onPrimary={() => setActiveTab('dataRoom')}
          onSecondary={() => setActiveTab('activity')}
        />
      )}
      {/* Progress Pipeline Stepper — AstorStepper (spec Stepper section) */}
      <GlassCard neonAccent={NeonAccentKey.NEUTRAL} sx={{ mb: 3, p: 2.25 }}>
        <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 2 }}>
          <Typography variant="subtitle2" color="textSecondary">
            {t('consumer.loanDetail.stepper.title', { defaultValue: 'Application status' })}
          </Typography>
          <GlassChip
            label={(() => {
              if (loanStage) {
                const consumerStage = toConsumerLoanStage(
                  loanStage as LoanStage,
                  (loanSubstage as LoanSubstage | undefined) ?? undefined,
                );
                return t(getConsumerLoanStageLabelKey(consumerStage));
              }
              return caseDetail.status
                ? t(`status.case.${caseDetail.status}`, caseDetail.status.replace(/_/g, ' '))
                : '';
            })()}
            size="small"
            color={getApplicationStatusColor(loanStage ?? caseDetail.status)}
          />
        </Box>
        <AstorStepper
          stages={pipelineSteps}
          current={terminalCase ? -1 : currentStep}
          inProgressLabel={t('consumer.loanDetail.stepper.inProgress', { defaultValue: 'In progress' })}
        />
      </GlassCard>
      {/* Loan Officer + SLA — kept above tabs for quick reference */}
      {(() => {
        return assigneeName || pipelineStatus ? (
          <Stack direction={{ xs: 'column', md: 'row' }} spacing={2} sx={{ mb: 3 }}>
            <Stack spacing={2} sx={{ flex: 1 }}>
              {assigneeName && (
                <GlassCard neonAccent={NeonAccentKey.NEUTRAL}>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
                    <Avatar sx={{ bgcolor: 'primary.main', width: 36, height: 36 }}>
                      <PersonIcon fontSize="small" />
                    </Avatar>
                    <Box>
                      <Typography variant="caption" color="textSecondary">
                        {t('case.yourLoanOfficer')}
                      </Typography>
                      <Typography variant="body1" sx={{ fontWeight: 500 }}>
                        {assigneeName}
                      </Typography>
                    </Box>
                  </Box>
                </GlassCard>
              )}
              {pipelineStatus &&
                (() => {
                  const slaOnTrack = pipelineStatus.status === 'ON_TRACK' || pipelineStatus.status === 'COMPLETED';
                  return (
                    <GlassCard neonAccent={NeonAccentKey.NEUTRAL}>
                      <Typography variant="caption" color="textSecondary">
                        {t('case.expectedTimeline')}
                      </Typography>
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mt: 0.5 }}>
                        <GlassChip
                          size="small"
                          label={slaOnTrack ? t('case.slaOnTrack') : t('case.slaAtRisk')}
                          color={slaOnTrack ? 'success' : 'warning'}
                          icon={slaOnTrack ? <CheckCircleIcon /> : <WarningIcon />}
                        />
                        {pipelineStatus.daysInStage != null && (
                          <Typography variant="caption" color="textSecondary">
                            {t('case.daysInStage', { days: pipelineStatus.daysInStage })}
                          </Typography>
                        )}
                      </Box>
                    </GlassCard>
                  );
                })()}
            </Stack>
          </Stack>
        ) : null;
      })()}
      {/* Inline Astor suggestion row (spec Astor surface #2) */}
      {!terminalCase && (
        <Stack
          direction="row"
          spacing={1.25}
          sx={{ mb: 3, px: 1.5, py: 1, borderRadius: 2, bgcolor: 'action.hover', alignItems: 'center' }}
        >
          <AstorAvatar size={20} />
          <Typography variant="body2" sx={{ flex: 1, color: 'text.primary' }}>
            {t('consumer.loanDetail.astor.suggestionsCount', {
              defaultValue: 'Astor has 3 suggestions to keep this loan moving.',
            })}
          </Typography>
          <GlassButton variant="text" size="small" onClick={() => setActiveTab('activity')}>
            {t('consumer.loanDetail.astor.review', { defaultValue: 'Review' })}
          </GlassButton>
        </Stack>
      )}
      {/* Main Page Tabs (spec order: Application / My property / Data room / Activity / Messages / Parties) */}
      <GlassTabs
        tabs={[
          {
            value: 'application',
            label: t('consumer.loanDetail.tabs.application', { defaultValue: 'Application' }),
            icon: <ApplicationIcon fontSize="small" />,
          },
          {
            value: 'myProperty',
            label: t('consumer.loanDetail.tabs.myProperty', { defaultValue: 'My property' }),
            icon: <HomeIcon fontSize="small" />,
          },
          {
            value: 'dataRoom',
            label: t('consumer.loanDetail.tabs.dataRoom', { defaultValue: 'Data room' }),
            icon: <FolderIcon fontSize="small" />,
          },
          {
            value: 'activity',
            label: t('consumer.loanDetail.tabs.activity', { defaultValue: 'Activity' }),
            icon: <TimelineIcon fontSize="small" />,
          },
          {
            value: 'messages',
            label: t('consumer.loanDetail.tabs.messages', { defaultValue: 'Messages' }),
            icon: <MessageIcon fontSize="small" />,
          },
          {
            value: 'parties',
            label: t('consumer.loanDetail.tabs.parties', { defaultValue: 'Parties' }),
            icon: <GroupsIcon fontSize="small" />,
          },
          ...(['closing', 'approved', 'funded', 'completed'].includes(caseDetail.status)
            ? [{ value: 'closing', label: t('case.closing'), icon: <GavelIcon fontSize="small" /> }]
            : []),
        ]}
        value={activeTab}
        onChange={(_, newValue) => setActiveTab(newValue as string)}
        size="medium"
        sx={{ mb: 3 }}
        data-testid="case-tabs"
      />
      {/* My Property Tab — borrower-facing property evaluation surface (#1623 Phase 2H) */}
      {activeTab === 'myProperty' && <MyPropertyTab caseId={caseDetail.id} />}
      {/* Application Tab - Shows loan details + submission details */}
      {activeTab === 'application' && (
        <>
          {/* Loan Details (from case metadata) — Issue #1145 */}
          {(() => {
            const loanMeta = caseMetadata ?? {};
            const subData = submissionData as any;
            const loanType = loanMeta.loanType ?? loanMeta.loan_type ?? subData?.loanType ?? subData?.loan_type;
            const loanAmount = loanMeta.loanAmount ?? loanMeta.requestedLoanAmount ?? subData?.loanAmount;
            const loanTerm = loanMeta.loanTerm ?? loanMeta.loan_term ?? subData?.loanTerm;
            const rate = loanMeta.calculatedRate ?? subData?.calculatedRate;
            const ltv = loanMeta.ltv ?? subData?.ltv;
            const purchasePrice = loanMeta.purchasePrice ?? subData?.purchasePrice;
            const arv = loanMeta.arv ?? loanMeta.afterRepairValue ?? subData?.arv;
            const rehabBudget = loanMeta.rehabBudget ?? loanMeta.renovationBudget ?? subData?.rehabBudget;
            const exitStrategy = loanMeta.exitStrategy ?? loanMeta.exit_strategy ?? subData?.exitStrategy;
            const estMonthly = loanMeta.estimatedMonthly ?? subData?.estimatedMonthly;
            // TODO(GH-03): wire DSCR from ListConsumerCases proto extension once backend exposes it
            const dscr = loanMeta.dscr ?? subData?.dscr;
            const propAddr = loanMeta.propertyAddress ?? subData?.propertyAddress;
            const propType = loanMeta.propertyType ?? loanMeta.investmentType ?? subData?.propertyType;
            const hasLoanData = loanAmount || loanType || rate || ltv || propAddr;

            const fmtCurr = (v: unknown) =>
              formatCurrencyWhole(
                typeof v === 'number' ? v : Number.parseFloat(String(v ?? '').replace(/[^0-9.]/g, '')),
              );
            const fmtPct = (v: unknown) =>
              formatRate(typeof v === 'number' ? v : Number.parseFloat(String(v ?? '')), 2);
            const fmtTitle = (v: unknown) => snakeToTitle(String(v)) ?? String(v);

            if (!hasLoanData) return null;
            return (
              <GlassCard neonAccent={NeonAccentKey.NEUTRAL} sx={{ mb: 2 }}>
                <Typography variant="h6" sx={{ mb: 2 }}>
                  {t('case.loanDetails')}
                </Typography>
                <Box
                  sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', sm: '1fr 1fr', md: '1fr 1fr 1fr' }, gap: 2 }}
                >
                  {loanType && (
                    <Box>
                      <Typography variant="caption" color="textSecondary">
                        {t('case.loanType')}
                      </Typography>
                      <Typography variant="body1" sx={{ fontWeight: 500 }}>
                        {fmtTitle(loanType)}
                      </Typography>
                    </Box>
                  )}
                  {loanAmount && (
                    <Box>
                      <Typography variant="caption" color="textSecondary">
                        {t('case.loanAmount')}
                      </Typography>
                      <Typography variant="body1" sx={{ fontWeight: 600 }}>
                        {fmtCurr(loanAmount)}
                      </Typography>
                    </Box>
                  )}
                  {loanTerm && (
                    <Box>
                      <Typography variant="caption" color="textSecondary">
                        {t('case.loanTerm')}
                      </Typography>
                      <Typography variant="body1">
                        {loanTerm} {t('common.months')}
                      </Typography>
                    </Box>
                  )}
                  {rate && (
                    <Box>
                      <Typography variant="caption" color="textSecondary">
                        {t('case.interestRate')}
                      </Typography>
                      <Typography variant="body1">{fmtPct(rate)}</Typography>
                    </Box>
                  )}
                  {ltv && (
                    <Box>
                      <Typography variant="caption" color="textSecondary">
                        {t('case.ltv')}
                      </Typography>
                      <Typography variant="body1">{fmtPct(ltv)}</Typography>
                    </Box>
                  )}
                  {purchasePrice && (
                    <Box>
                      <Typography variant="caption" color="textSecondary">
                        {t('case.purchasePrice')}
                      </Typography>
                      <Typography variant="body1">{fmtCurr(purchasePrice)}</Typography>
                    </Box>
                  )}
                  {arv && (
                    <Box>
                      <Typography variant="caption" color="textSecondary">
                        {t('case.arv')}
                      </Typography>
                      <Typography variant="body1">{fmtCurr(arv)}</Typography>
                    </Box>
                  )}
                  {rehabBudget && (
                    <Box>
                      <Typography variant="caption" color="textSecondary">
                        {t('case.rehabBudget')}
                      </Typography>
                      <Typography variant="body1">{fmtCurr(rehabBudget)}</Typography>
                    </Box>
                  )}
                  {exitStrategy && (
                    <Box>
                      <Typography variant="caption" color="textSecondary">
                        {t('case.exitStrategy')}
                      </Typography>
                      <Typography variant="body1">{fmtTitle(exitStrategy)}</Typography>
                    </Box>
                  )}
                  {estMonthly && (
                    <Box>
                      <Typography variant="caption" color="textSecondary">
                        {t('case.estimatedMonthly')}
                      </Typography>
                      <Typography variant="body1">{fmtCurr(estMonthly)}</Typography>
                    </Box>
                  )}
                  <Box>
                    <Typography variant="caption" color="textSecondary">
                      {t('case.dscr', { defaultValue: 'DSCR' })}
                    </Typography>
                    <Typography variant="body2" sx={{ fontWeight: 600 }}>
                      {typeof dscr === 'number' && Number.isFinite(dscr)
                        ? `${dscr.toFixed(2)}×`
                        : dscr
                          ? (() => {
                              const n = Number.parseFloat(String(dscr));
                              return Number.isFinite(n) ? `${n.toFixed(2)}×` : '—';
                            })()
                          : '—'}
                    </Typography>
                  </Box>
                </Box>
                {propAddr && (
                  <Box sx={{ mt: 2 }}>
                    <Typography variant="caption" color="textSecondary">
                      {t('case.propertyAddress')}
                    </Typography>
                    <Typography variant="body2">
                      {typeof propAddr === 'object' && propAddr !== null
                        ? [
                            (propAddr as any).street1,
                            (propAddr as any).city,
                            (propAddr as any).state,
                            (propAddr as any).postalCode,
                          ]
                            .filter(Boolean)
                            .join(', ')
                        : String(propAddr)}
                      {propType ? ` (${fmtTitle(propType)})` : ''}
                    </Typography>
                  </Box>
                )}
              </GlassCard>
            );
          })()}

          {submissionValues ? (
            <GlassCard neonAccent={NeonAccentKey.NEUTRAL}>
              <Typography variant="h6" sx={{ mb: 2 }}>
                {t('case.yourSubmission')}
              </Typography>
              {template && !templateLoading ? (
                <IntakeSubmissionSummary
                  template={template}
                  values={submissionValues}
                  showSections={true}
                  showEmptyFields={false}
                  title=""
                  neonAccent={NeonAccentKey.NEUTRAL}
                />
              ) : templateLoading ? (
                <Box sx={{ display: 'flex', justifyContent: 'center', py: 3 }}>
                  <ShimmerLoader variant="circular" width={24} height={24} />
                </Box>
              ) : (
                // Fallback: Display as simple key-value pairs when no template available
                <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1.5 }}>
                  {Object.entries(submissionValues).map(([key, value]) => (
                    <Box key={key}>
                      <Typography variant="caption" color="textSecondary" sx={{ textTransform: 'capitalize' }}>
                        {key.replace(/([A-Z])/g, ' $1').trim()}
                      </Typography>
                      <Typography variant="body2">
                        {typeof value === 'object' ? JSON.stringify(value) : String(value)}
                      </Typography>
                    </Box>
                  ))}
                </Box>
              )}
            </GlassCard>
          ) : (
            <GlassCard neonAccent={NeonAccentKey.NEUTRAL}>
              <Typography color="textSecondary">{t('case.noSubmissionData')}</Typography>
            </GlassCard>
          )}
        </>
      )}
      {/* Data Room Tab (Item #5 - Interconnected Data Room) */}
      {activeTab === 'dataRoom' && (
        <Box sx={{ flex: 1, minHeight: 'max(500px, calc(100vh - 420px))', display: 'flex', flexDirection: 'column' }}>
          <SplitPanel
            panelOpen={dataRoomUploadOpen || browseOpen}
            onPanelClose={() => {
              if (browseOpen) handleBrowseClose();
              else handleConsumerUploadClose();
            }}
            panelWidth={480}
            panelAriaLabel={browseOpen ? (t('dataRoom.browse') ?? 'Browse') : (t('documents.upload') ?? 'Upload')}
            panel={
              browseOpen ? (
                <ConsumerBrowsePanel
                  caseId={effectiveCaseId!}
                  onClose={handleBrowseClose}
                  onLink={handleLinkDocument}
                  isLinking={linkDocumentMutation.isPending}
                />
              ) : dataRoomUploadOpen ? (
                <ConsumerUploadPanel
                  caseId={effectiveCaseId!}
                  defaultDocumentType={pendingUploadDocType ?? undefined}
                  onClose={handleConsumerUploadClose}
                  onUpload={handleConsumerUpload}
                  isUploading={drIsUploading}
                />
              ) : null
            }
          >
            <GlassCard neonAccent={NeonAccentKey.NEUTRAL}>
              <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
                <Typography variant="h6">{t('case.dataRoom')}</Typography>
                <Stack direction="row" spacing={1}>
                  <GlassButton size="small" variant="outlined" onClick={handleConsumerUploadOpen}>
                    {t('dataRoom.upload') ?? 'Upload'}
                  </GlassButton>
                  <GlassButton size="small" variant="contained" onClick={handleBrowseOpen}>
                    {t('dataRoom.browse') ?? 'Browse & Link'}
                  </GlassButton>
                </Stack>
              </Box>

              {/* Loading state */}
              {dataRoomLoading && (
                <Box sx={{ display: 'flex', justifyContent: 'center', p: 4 }}>
                  <ShimmerLoader variant="circular" width={40} height={40} />
                </Box>
              )}

              {/* Error state */}
              {dataRoomError && (
                <GlassAlert severity="error" visible sx={{ mb: 2 }}>
                  {dataRoomError.message}
                </GlassAlert>
              )}

              {/* Link mutation error */}
              {linkDocumentMutation.isError && (
                <GlassAlert severity="error" visible sx={{ mb: 2 }}>
                  {linkDocumentMutation.error.message}
                </GlassAlert>
              )}

              {!dataRoomLoading && !dataRoomError && (
                <>
                  {/* Linked Documents + Source Groups via DataRoomPanel */}
                  {(() => {
                    const VERIFICATION_MAP: Record<
                      string,
                      'verified' | 'pending' | 'rejected' | 'expired' | 'needs_reupload'
                    > = {
                      VERIFIED: 'verified',
                      PENDING: 'pending',
                      REJECTED: 'rejected',
                      EXPIRED: 'expired',
                      NEEDS_REUPLOAD: 'needs_reupload',
                    };
                    const loanDocuments = (dataRoomData?.loanDocuments ?? []).map((doc: any) => ({
                      documentId: doc.documentId ?? doc.id ?? '',
                      name: doc.document?.name ?? doc.document?.originalName ?? doc.documentId ?? 'Document',
                      documentType: doc.documentType ?? '',
                      scope: (doc.scopeOverride === 'loan' ||
                      doc.scopeOverride === 'personal' ||
                      doc.scopeOverride === 'entity'
                        ? doc.scopeOverride
                        : ((doc.documentType ? DOCUMENT_SCOPE_MAP[doc.documentType as DocumentType] : undefined) ??
                          'loan')) as 'loan' | 'personal' | 'entity',
                      verificationStatus: doc.verificationStatus ? VERIFICATION_MAP[doc.verificationStatus] : undefined,
                      rejectionReason: doc.rejectionReason ?? undefined,
                    }));

                    const sources: DataRoomPanelSourceGroup[] = (dataRoomData?.sources ?? []).map((source: any) => {
                      const key = `${source.sourceEntityType}:${source.sourceEntityId}`;
                      const translationKey = `dataRoom.source.${source.sourceLabel}`;
                      const translatedLabel = t(translationKey as any);
                      const resolvedLabel =
                        translatedLabel && translatedLabel !== translationKey
                          ? translatedLabel
                          : (source.sourceLabel ?? source.sourceName ?? '');
                      return {
                        sourceEntityType: (source.sourceEntityType ?? '') as string,
                        sourceEntityId: (source.sourceEntityId ?? '') as string,
                        sourceName: (source.sourceName ?? '') as string,
                        sourceLabel: resolvedLabel as string,
                        documents: (source.documents || []).map((doc: any) => ({
                          documentId: doc.documentId ?? doc.id ?? '',
                          name: doc.document?.name ?? doc.document?.originalName ?? doc.documentId ?? 'Document',
                          documentType: doc.documentType ?? '',
                          scope: (doc.scopeOverride === 'loan' ||
                          doc.scopeOverride === 'personal' ||
                          doc.scopeOverride === 'entity'
                            ? doc.scopeOverride
                            : ((doc.documentType ? DOCUMENT_SCOPE_MAP[doc.documentType as DocumentType] : undefined) ??
                              'personal')) as 'loan' | 'personal' | 'entity',
                          verificationStatus: doc.verificationStatus
                            ? VERIFICATION_MAP[doc.verificationStatus]
                            : undefined,
                          rejectionReason: doc.rejectionReason ?? undefined,
                        })),
                        isExpanded: expandedSources[key] ?? true,
                        onToggle: () =>
                          setExpandedSources((prev) => ({
                            ...prev,
                            [key]: !(prev[key] ?? true),
                          })),
                      };
                    });

                    // Issue #1145: Map required documents from checklist for merged data room view
                    const requiredDocuments = (dataRoomData?.requiredDocuments ?? []).map((doc: any) => ({
                      checklistItemId: doc.checklistItemId,
                      documentType: doc.documentType ?? '',
                      displayName: doc.displayName ?? '',
                      description: doc.description,
                      category: doc.category,
                      isRequired: doc.isRequired ?? true,
                      status: doc.status as 'missing' | 'pending' | 'verified' | 'rejected' | 'needs_reupload',
                      attachment: doc.attachment
                        ? {
                            id: doc.attachment.documentId ?? doc.attachment.id ?? '',
                            name: doc.attachment.document?.name ?? doc.displayName,
                            documentType: doc.attachment.documentType ?? doc.documentType,
                            scope: (doc.attachment.scopeOverride === 'loan' ||
                            doc.attachment.scopeOverride === 'personal' ||
                            doc.attachment.scopeOverride === 'entity'
                              ? doc.attachment.scopeOverride
                              : 'loan') as 'loan' | 'personal' | 'entity',
                            verificationStatus: doc.attachment.verificationStatus
                              ? VERIFICATION_MAP[doc.attachment.verificationStatus]
                              : undefined,
                            rejectionReason: doc.attachment.rejectionReason ?? undefined,
                          }
                        : null,
                    }));

                    // Issue #1145: Checklist progress for overlay
                    const progress = dataRoomData?.checklistProgress as any;
                    const checklistProgress = progress
                      ? {
                          fulfilled: progress.completedRequiredItems ?? 0,
                          total: progress.requiredItems ?? 0,
                          missingItems: (progress.items ?? [])
                            .filter((i: any) => i.isRequired && !i.isFulfilled)
                            .map((i: any) => i.displayName),
                        }
                      : undefined;

                    const hasDocuments =
                      loanDocuments.length > 0 ||
                      sources.some((s) => s.documents.length > 0) ||
                      requiredDocuments.length > 0;

                    if (!hasDocuments) {
                      return <DataRoomEmptyState onBrowse={handleBrowseOpen} onUpload={handleConsumerUploadOpen} />;
                    }

                    return (
                      <>
                        <DataRoomPanel
                          loanDocuments={loanDocuments}
                          sources={sources}
                          checklistProgress={checklistProgress}
                          requiredDocuments={requiredDocuments}
                          onUploadForRequirement={(_checklistItemId, documentType) => {
                            setPendingUploadDocType(documentType || null);
                            handleConsumerUploadOpen();
                          }}
                          onView={handleViewDocument}
                          onBrowse={handleBrowseOpen}
                          onUpload={handleConsumerUploadOpen}
                        />
                      </>
                    );
                  })()}
                </>
              )}
            </GlassCard>
          </SplitPanel>
        </Box>
      )}
      {/* Activity Tab */}
      {activeTab === 'activity' && (
        <GlassCard neonAccent={NeonAccentKey.NEUTRAL}>
          <Typography variant="h6" gutterBottom>
            {t('section.activity')}
          </Typography>
          <EntityActivityTimeline
            events={timeline}
            maxItems={50}
            showCard={false}
            groupByTimeOfDay={true}
            showThreading={true}
            showAvatars={false}
            relativeTime={true}
            emptyMessage={t('timeline.noActivity')}
            neonAccent={NeonAccentKey.NEUTRAL}
          />
        </GlassCard>
      )}
      {/* Messages Tab */}
      {activeTab === 'messages' && (
        <GlassCard neonAccent={NeonAccentKey.NEUTRAL}>
          <Box sx={{ display: 'flex', flexDirection: 'column', minHeight: 400 }}>
            {/* Messages List */}
            <Box sx={{ flex: 1, overflow: 'auto', p: 2, maxHeight: 320 }}>
              {messagesLoading ? (
                <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
                  <ShimmerLoader variant="circular" width={40} height={40} />
                </Box>
              ) : !messagesData?.data?.length ? (
                <EmptyState
                  icon={<MessageIcon />}
                  title={t('messages.noConversation')}
                  description={t('case.messageDescription')}
                />
              ) : (
                <List>
                  {messagesData.data.map(
                    (msg: {
                      id: string;
                      sender: 'consumer' | 'business';
                      senderName?: string;
                      content: string;
                      createdAt: string;
                    }) => {
                      const isOwn = msg.sender === 'consumer';
                      return (
                        <ListItem
                          key={msg.id}
                          sx={{
                            flexDirection: isOwn ? 'row-reverse' : 'row',
                            gap: 2,
                            py: 1,
                          }}
                        >
                          <Avatar
                            sx={{
                              bgcolor: isOwn ? 'primary.main' : 'secondary.main',
                              width: 32,
                              height: 32,
                            }}
                          >
                            {isOwn ? 'Y' : 'S'}
                          </Avatar>
                          <Box
                            sx={{
                              p: 2,
                              maxWidth: '70%',
                              bgcolor: isOwn ? 'primary.dark' : 'action.selected',
                              borderRadius: 2,
                              color: 'text.primary',
                            }}
                          >
                            <ListItemText
                              primary={msg.content}
                              secondary={new Date(msg.createdAt).toLocaleTimeString([], {
                                hour: '2-digit',
                                minute: '2-digit',
                              })}
                              slotProps={{
                                primary: { sx: { color: 'text.primary' } },
                                secondary: {
                                  variant: 'caption',
                                  sx: { mt: 0.5, color: 'text.secondary' },
                                },
                              }}
                            />
                          </Box>
                        </ListItem>
                      );
                    },
                  )}
                  <div ref={messagesEndRef} />
                </List>
              )}
            </Box>

            <Divider />

            {/* Message Input */}
            <Box sx={{ p: 2, display: 'flex', gap: 2 }}>
              <MagicInput
                placeholder={t('placeholder.typeMessage')}
                value={messageInput}
                onChange={(e) => setMessageInput(e.target.value)}
                onKeyDown={(e) => {
                  if (e.key === 'Enter' && !e.shiftKey) {
                    e.preventDefault();
                    if (messageInput.trim() && effectiveCaseId) {
                      sendMessageMutation.mutate(
                        { caseId: effectiveCaseId, content: messageInput.trim() },
                        {
                          onSuccess: () => {
                            setMessageInput('');
                            messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
                          },
                        },
                      );
                    }
                  }
                }}
                size="small"
                disabled={sendMessageMutation.isPending}
                multiline
                maxRows={3}
              />
              <GlassButton
                variant="gradient"
                onClick={() => {
                  if (messageInput.trim() && effectiveCaseId) {
                    sendMessageMutation.mutate(
                      { caseId: effectiveCaseId, content: messageInput.trim() },
                      {
                        onSuccess: () => {
                          setMessageInput('');
                          messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
                        },
                      },
                    );
                  }
                }}
                endIcon={
                  sendMessageMutation.isPending ? (
                    <ShimmerLoader variant="circular" width={16} height={16} />
                  ) : (
                    <SendIcon />
                  )
                }
                disabled={!messageInput.trim() || sendMessageMutation.isPending}
              >
                {t('messages.send')}
              </GlassButton>
            </Box>
          </Box>
        </GlassCard>
      )}
      {/* Parties Tab (Gap #7, Item #11 — read-only borrowers & guarantors) */}
      {activeTab === 'parties' && (
        <Stack spacing={3}>
          {/* Borrowers Section */}
          <GlassCard neonAccent={NeonAccentKey.NEUTRAL}>
            <Typography variant="h6" gutterBottom>
              {t('case.borrowers')}
            </Typography>
            {borrowersData?.data && borrowersData.data.length > 0 ? (
              <List disablePadding>
                {borrowersData.data.map((borrower: any) => {
                  const roleLabels: Record<string, string> = {
                    primary_borrower: t('parties.role.primaryBorrower'),
                    co_borrower: t('parties.role.coBorrower'),
                    sponsor: t('parties.role.sponsor'),
                  };
                  return (
                    <ListItem key={borrower.id} divider sx={{ px: 0 }}>
                      <ListItemIcon>
                        <PersonIcon />
                      </ListItemIcon>
                      <ListItemText
                        primary={borrower.entityName || borrower.borrowingEntityId}
                        slotProps={{ secondary: { component: 'div' } }}
                        secondary={
                          <>
                            {roleLabels[borrower.role] || borrower.role}
                            <Typography
                              variant="body2"
                              component="span"
                              sx={{ display: 'block', color: 'text.secondary' }}
                            >
                              {getRoleDescription(borrower.role)}
                            </Typography>
                          </>
                        }
                      />
                      {borrower.ownershipInterest != null && (
                        <GlassChip
                          label={`${borrower.ownershipInterest}% ${t('parties.ownershipInterest')}`}
                          size="small"
                          color="info"
                        />
                      )}
                    </ListItem>
                  );
                })}
              </List>
            ) : (
              <EmptyState icon={<PersonIcon />} title={t('case.borrowers')} description={t('case.noBorrowers')} />
            )}
          </GlassCard>

          {/* Guarantors Section */}
          <GlassCard neonAccent={NeonAccentKey.NEUTRAL}>
            <Typography variant="h6" gutterBottom>
              {t('case.guarantors')}
            </Typography>
            {guarantorsData?.data && guarantorsData.data.length > 0 ? (
              <List disablePadding>
                {guarantorsData.data.map((guarantor: any) => (
                  <ListItem key={guarantor.id} divider sx={{ px: 0 }}>
                    <ListItemIcon>
                      <PersonIcon />
                    </ListItemIcon>
                    <ListItemText
                      primary={
                        <ButtonBase
                          onClick={() => navigate('/guarantors')}
                          sx={{
                            color: 'primary.main',
                            fontWeight: 600,
                            fontSize: '1rem',
                            textAlign: 'left',
                            '&:hover': { textDecoration: 'underline' },
                          }}
                        >
                          {guarantor.guarantorName || guarantor.consumerId || guarantor.borrowingEntityId}
                        </ButtonBase>
                      }
                      secondary={
                        guarantor.guaranteeType
                          ? getGuaranteeDescription(guarantor.guaranteeType, guarantor.guaranteeAmount)
                          : t('parties.role.guarantor')
                      }
                    />
                    <Box sx={{ display: 'flex', gap: 1 }}>
                      {guarantor.status && (
                        <GlassChip
                          label={guarantor.status}
                          size="small"
                          color={guarantor.status === 'active' ? 'success' : 'default'}
                        />
                      )}
                    </Box>
                  </ListItem>
                ))}
              </List>
            ) : (
              <EmptyState icon={<PersonIcon />} title={t('case.guarantors')} description={t('case.noGuarantors')} />
            )}
          </GlassCard>
        </Stack>
      )}
      {/* Closing Tab (Item #10 — read-only consumer view) */}
      {activeTab === 'closing' && (
        <GlassCard neonAccent={NeonAccentKey.NEUTRAL}>
          <Typography variant="h6" gutterBottom>
            {t('case.closing')}
          </Typography>
          <GlassAlert severity="info" visible={true} sx={{ mb: 2 }}>
            {t('case.closingReadOnly')}
          </GlassAlert>

          {/* Closing details from case metadata */}
          {(() => {
            const closingMeta = (caseMetadata?.closing ?? {}) as Record<string, unknown>;
            const hasClosingData = closingMeta && Object.keys(closingMeta).length > 0;

            if (!hasClosingData) {
              return (
                <EmptyState icon={<GavelIcon />} title={t('case.closing')} description={t('case.noClosingData')} />
              );
            }

            return (
              <Stack spacing={3}>
                {/* Closing Schedule */}
                {closingMeta.targetDate ? (
                  <Box>
                    <Typography variant="subtitle2" color="textSecondary">
                      {t('case.closingSchedule')}
                    </Typography>
                    <Typography variant="body1">
                      {new Date(closingMeta.targetDate as string).toLocaleDateString()}
                    </Typography>
                  </Box>
                ) : null}

                {/* Closing Checklist */}
                {Array.isArray(closingMeta.checklist) && (closingMeta.checklist as any[]).length > 0 ? (
                  <Box>
                    <Typography variant="subtitle2" color="textSecondary" gutterBottom>
                      {t('case.closingChecklist')}
                    </Typography>
                    <List disablePadding>
                      {(closingMeta.checklist as any[]).map((item: any, idx: number) => (
                        <ListItem key={item.id ?? idx} sx={{ px: 0 }}>
                          <ListItemIcon>
                            {item.completed ? <CheckCircleIcon color="success" /> : <DocIcon color="disabled" />}
                          </ListItemIcon>
                          <ListItemText primary={item.title ?? item.name} />
                        </ListItem>
                      ))}
                    </List>
                  </Box>
                ) : null}

                {/* Wire Instructions (partially masked) */}
                {closingMeta.wireInstructions ? (
                  <Box>
                    <Typography variant="subtitle2" color="textSecondary" gutterBottom>
                      {t('case.wireInstructions')}
                    </Typography>
                    {(() => {
                      const wire = closingMeta.wireInstructions as Record<string, string>;
                      return (
                        <Stack spacing={1}>
                          {wire.bankName && (
                            <Box>
                              <Typography variant="caption" color="textSecondary">
                                {t('closing.bankName')}
                              </Typography>
                              <Typography variant="body2">{wire.bankName}</Typography>
                            </Box>
                          )}
                          {wire.routingNumber && (
                            <Box>
                              <Typography variant="caption" color="textSecondary">
                                {t('closing.routingNumber')}
                              </Typography>
                              <Typography variant="body2">{wire.routingNumber}</Typography>
                            </Box>
                          )}
                          {wire.accountNumber && (
                            <Box>
                              <Typography variant="caption" color="textSecondary">
                                {t('closing.accountNumber')}
                              </Typography>
                              <Typography variant="body2">{wire.accountNumber}</Typography>
                            </Box>
                          )}
                        </Stack>
                      );
                    })()}
                  </Box>
                ) : null}
              </Stack>
            );
          })()}
        </GlassCard>
      )}
      {/* Related Proposals Section (Issue #475) */}
      {proposalsData?.data && proposalsData.data.length > 0 && (
        <GlassCard neonAccent={NeonAccentKey.NEUTRAL} sx={{ mt: 3 }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 2 }}>
            <ProposalIcon color="primary" />
            <Typography variant="h6">{t('case.relatedProposals')}</Typography>
          </Box>
          <List disablePadding>
            {proposalsData.data.map((proposal) => {
              const statusColors: Record<string, 'default' | 'primary' | 'success' | 'warning' | 'error'> = {
                draft: 'default',
                sent: 'primary',
                viewed: 'primary',
                accepted: 'success',
                rejected: 'error',
                expired: 'warning',
              };
              const statusLabels: Record<string, string> = {
                draft: t('proposal.status.draft'),
                sent: t('proposal.status.sent'),
                viewed: t('proposal.status.viewed'),
                accepted: t('proposal.status.accepted'),
                rejected: t('proposal.status.rejected'),
                expired: t('proposal.status.expired'),
              };

              return (
                <ListItem key={proposal.id} divider sx={{ px: 0 }}>
                  <ListItemIcon>
                    <ProposalIcon />
                  </ListItemIcon>
                  <ListItemText
                    primary={proposal.title}
                    secondary={
                      proposal.sentAt
                        ? t('proposal.received', { date: new Date(proposal.sentAt).toLocaleDateString() })
                        : undefined
                    }
                  />
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                    <GlassChip
                      label={statusLabels[proposal.status] || proposal.status}
                      size="small"
                      color={statusColors[proposal.status] || 'default'}
                    />
                    <GlassButton variant="outlined" size="small" onClick={() => navigate(`/proposals/${proposal.id}`)}>
                      {t('case.viewProposal')}
                    </GlassButton>
                  </Box>
                </ListItem>
              );
            })}
          </List>
        </GlassCard>
      )}
      {/* Show loading indicator for proposals */}
      {proposalsLoading && (
        <GlassCard neonAccent={NeonAccentKey.NEUTRAL} sx={{ mt: 3 }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
            <ShimmerLoader variant="circular" width={20} height={20} />
            <Typography>{t('common.loading')}</Typography>
          </Box>
        </GlassCard>
      )}
      {/* E-Signatures Section (Issue #300) */}
      {esignSubmissions?.submissions && esignSubmissions.submissions.length > 0 && (
        <GlassCard neonAccent={NeonAccentKey.NEUTRAL} sx={{ mt: 3 }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 2 }}>
            <DrawIcon color="warning" />
            <Typography variant="h6">{t('case.documentsRequiringSignature')}</Typography>
          </Box>
          <List disablePadding>
            {esignSubmissions.submissions.map((submission: any) => {
              // Find the submitter entry for this consumer
              const mySubmitter = submission.submitters?.find((s: any) => PENDING_SUBMITTER_STATUSES.has(s.status));
              const isCompleted = submission.status === 'completed';
              const canSign = mySubmitter && !isCompleted;

              return (
                <ListItem key={submission.id} divider sx={{ flexDirection: 'column', alignItems: 'stretch', py: 2 }}>
                  <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', width: '100%' }}>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                      <DrawIcon />
                      <Box>
                        <Typography variant="body1" sx={{ fontWeight: 500 }}>
                          {t('esign.signingRequest', { id: submission.id })}
                        </Typography>
                        <Typography variant="body2" color="textSecondary">
                          {t('esign.requested', { date: new Date(submission.created_at).toLocaleDateString() })}
                        </Typography>
                      </Box>
                    </Box>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                      <SigningStatusDisplay status={submission.status} compact />
                      {canSign && (
                        <GlassButton
                          variant="glow"
                          size="small"
                          onClick={() => {
                            setSelectedSubmitterId(mySubmitter.id);
                            setSigningModalOpen(true);
                          }}
                        >
                          {t('esign.signNow')}
                        </GlassButton>
                      )}
                    </Box>
                  </Box>
                </ListItem>
              );
            })}
          </List>
        </GlassCard>
      )}
      {/* Document Signing Modal */}
      <DocumentSigningModal
        open={signingModalOpen}
        onClose={() => {
          setSigningModalOpen(false);
          setSelectedSubmitterId(null);
        }}
        submitterId={selectedSubmitterId}
        onSigningComplete={() => {
          refetchEsignSubmissions();
          setSigningModalOpen(false);
          setSelectedSubmitterId(null);
        }}
      />
      {/* Upload Dialog */}
      <Dialog
        open={uploadDialogOpen}
        onClose={() => !uploadMutation.isPending && setUploadDialogOpen(false)}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle>
          {t('documents.uploadDialog.title')}
          <Typography variant="body2" color="textSecondary">
            {t('documents.uploadDialog.filesSelected', { count: selectedFiles.length })}
          </Typography>
        </DialogTitle>
        <DialogContent>
          <Box sx={{ pt: 1, display: 'flex', flexDirection: 'column', gap: 2 }}>
            {/* File List */}
            <Box
              sx={{
                maxHeight: 200,
                overflow: 'auto',
                border: '1px solid',
                borderColor: 'divider',
                borderRadius: 1,
              }}
            >
              {selectedFiles.map((file, index) => (
                <Box
                  key={file.name}
                  sx={{
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'space-between',
                    p: 1,
                    borderBottom: index < selectedFiles.length - 1 ? '1px solid' : 'none',
                    borderColor: 'divider',
                  }}
                >
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                    <DocIcon />
                    <Box>
                      <Typography variant="body2" noWrap sx={{ maxWidth: 200 }}>
                        {file.name}
                      </Typography>
                      <Typography variant="caption" color="textSecondary">
                        {formatFileSize(file.size)}
                      </Typography>
                    </Box>
                  </Box>
                  {uploadProgress[file.name] !== undefined ? (
                    <Box sx={{ width: 100 }}>
                      <LinearProgress variant="determinate" value={uploadProgress[file.name] ?? 0} />
                    </Box>
                  ) : (
                    <GlassIconButton size="small" onClick={() => handleRemoveFile(index)}>
                      <CloseIcon fontSize="small" />
                    </GlassIconButton>
                  )}
                </Box>
              ))}
            </Box>

            <MagicSelect
              fullWidth
              label={t('documents.uploadDialog.selectCategory')}
              value={selectedCategory}
              onChange={(e) => setSelectedCategory(e.target.value as string)}
              options={DOCUMENT_CATEGORY_KEYS.map((cat) => ({
                value: cat.value,
                label: t(cat.labelKey),
              }))}
            />
          </Box>
        </DialogContent>
        <DialogActions>
          <GlassButton onClick={() => setUploadDialogOpen(false)} disabled={uploadMutation.isPending}>
            {t('common.cancel')}
          </GlassButton>
          <GlassButton
            variant="gradient"
            onClick={() => void handleUpload()}
            disabled={uploadMutation.isPending || selectedFiles.length === 0}
            startIcon={
              uploadMutation.isPending ? <ShimmerLoader variant="circular" width={20} height={20} /> : <UploadIcon />
            }
            data-testid="upload-confirm"
          >
            {uploadMutation.isPending
              ? t('documents.uploadDialog.uploading')
              : t('documents.uploadDialog.uploadFiles', { count: selectedFiles.length })}
          </GlassButton>
        </DialogActions>
      </Dialog>
      {/* Withdraw Confirmation Dialog (Issue #809) */}
      <Dialog open={withdrawDialogOpen} onClose={handleWithdrawClose} maxWidth="xs" fullWidth>
        <DialogTitle>{t('application.withdrawTitle')}</DialogTitle>
        <DialogContent>
          <Typography sx={{ mb: 2 }}>{t('application.confirmWithdraw')}</Typography>
          <GlassTextField
            fullWidth
            multiline
            rows={3}
            placeholder={t('application.withdrawReason')}
            value={withdrawReason}
            onChange={(e: React.ChangeEvent<HTMLInputElement>) => setWithdrawReason(e.target.value)}
          />
        </DialogContent>
        <DialogActions>
          <GlassButton onClick={handleWithdrawClose} disabled={withdrawMutation.isPending}>
            {t('common.cancel')}
          </GlassButton>
          <GlassButton
            variant="gradient"
            color="error"
            onClick={handleWithdrawConfirm}
            disabled={withdrawMutation.isPending}
          >
            {withdrawMutation.isPending ? t('common.loading') : t('application.withdraw')}
          </GlassButton>
        </DialogActions>
      </Dialog>
    </PageTemplate>
  );
}
